import React, { useMemo } from 'react';

import { AssetClass, ResourceType } from '@/cip/_openapi/portfolio-optimizer';
import { useTradeHistory } from '@/cip/api/tradeHistory';
import { tradeHistoryListColumns } from '@/cip/TradeHistory/tradeHistoryListColumns';
import { AssetClassBadge } from '@/components/AssetClassBadge';
import { ServerDataTable } from '@/components/table/DataTable';
import { TradeSideBadge } from '@/components/TradeSideBadge';
import { useRequiredParams } from 'utils';

import { AccountNavCash } from './AccountNavCash';
import {
    accountTableClassName,
    AccountTableLayout,
    DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
} from './accountTableDefaults';
import { AccountTableToolbar } from './AccountTableToolbar';
import { LargerFlagCell } from './LargerFlagCell';
import { useAccountTableState } from './useAccountTableState';

// visible: tradeDate, symbol, description, side, quantity, assetClass, grossExposure, tradePrice, tradeValue, commission
const TRADES_DEFAULT_COLUMN_VISIBILITY = {
    ...DEFAULT_ACCOUNT_TABLE_COLUMN_VISIBILITY,
    tradeDate: true, // override base (hidden by default) to show it
    assetType: false,
    subAssetType: false,
    netExposure: false,
    ndvrTradeId: false,
    tradeStatus: false,
    orderType: false,
    primaryExchange: false,
    cusip: false,
    isin: false,
    allocationType: false,
    investmentSleeve: false,
    maturityDate: false,
    gicsCode: false,
    gicsSector: false,
    gicsIndustry: false,
};

const EXCLUDE_TRADE_COLUMNS = ['pId', 'portfolioGuid', 'portfolioName', 'householdName', 'aC', 'referencePortfolio'];

export const AccountTradesTab = ({ layout = 'legacy' }: { layout?: AccountTableLayout }) => {
    const { accountGUID } = useRequiredParams(['accountGUID']);

    const {
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        resetSettings,
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        accountFilter,
        allFilters,
    } = useAccountTableState({
        tableId: layout === 'new' ? 'accountNewTrades' : 'accountTrades',
        accountGUID,
        excludeColumns: EXCLUDE_TRADE_COLUMNS,
        defaultColumnVisibility: TRADES_DEFAULT_COLUMN_VISIBILITY,
        defaultColumnOrder: DEFAULT_ACCOUNT_TABLE_COLUMN_ORDER,
    });

    const tradesColumnsWithCustomRenderers = useMemo(
        () =>
            tradeHistoryListColumns.map((col) => {
                if (col.id === 'country') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => string }) => <LargerFlagCell alpha3={getValue()} />,
                    };
                }
                if (col.id === 'assetClass') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => AssetClass }) => (
                            <AssetClassBadge assetClass={getValue()} />
                        ),
                    };
                }
                if (col.id === 'side') {
                    return {
                        ...col,
                        cell: ({ getValue }: { getValue: () => string }) => <TradeSideBadge side={getValue()} />,
                    };
                }
                return col;
            }),
        [],
    );

    const {
        data: tradesPage,
        isLoading: isTradesLoading,
        isFetching: isTradesFetching,
    } = useTradeHistory({
        columnFilters: allFilters,
        pagination,
        sorting,
    });

    return (
        <ServerDataTable
            data={tradesPage?.trades || []}
            columns={tradesColumnsWithCustomRenderers}
            state={{ pagination, sorting, columnFilters, columnVisibility, columnOrder, columnSizing }}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            onColumnFiltersChange={setColumnFilters}
            onColumnVisibilityChange={setColumnVisibility}
            onColumnOrderChange={setColumnOrder}
            onColumnSizingChange={setColumnSizing}
            isLoading={isTradesLoading}
            isFetching={isTradesFetching}
            emptyMessage="No trades found"
            className={accountTableClassName(layout)}
            rowCount={tradesPage?.totalNr ?? 0}
            deferInitialRender={true}
            renderToolbar={(table) => (
                <AccountTableToolbar
                    table={table}
                    layout={layout}
                    resourceType={ResourceType.TRADE_HISTORY}
                    additionalFilters={accountFilter}
                    excludeColumns={EXCLUDE_TRADE_COLUMNS}
                    onReset={resetSettings}
                    leftContent={<AccountNavCash accountGUID={accountGUID} />}
                />
            )}
        />
    );
};
